/**
 * Donation validation — the single source of truth for /donate-form field rules.
 *
 * Framework-agnostic and dependency-free (no Zod) so it can be unit-tested in
 * isolation and bound to react-hook-form via `donationResolver`. Authored in
 * TypeScript so callers get real typed inference (`DonationValues`,
 * `DonationErrors`); Vite/esbuild transpiles it for the JS app and vitest.
 */

export type RegionCode = "IN" | "US";

export interface DonationValues {
  amount: string; // raw string straight from the controlled number input
  fullName: string;
  email: string;
  mobile: string;
  address: string;
  pan: string;
  termsAccepted: boolean;
}

export interface DonationContext {
  region: RegionCode;
  /** India-only: donor opted to claim 80G, which makes PAN required. */
  claim80G: boolean;
}

export type DonationErrors = Partial<Record<keyof DonationValues, string>>;

/** Minimum accepted gift per rail, in the rail's own currency units. */
export const MIN_AMOUNT: Record<RegionCode, number> = { IN: 100, US: 1 };

/** Sanity ceiling — gifts above this are routed to a human, not the web form. */
export const MAX_AMOUNT = 10_000_000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/**
 * Parse a raw amount into a positive, finite number — or `null` if it is empty,
 * non-numeric (NaN), zero, or negative. This is the reconciliation primitive:
 * preset tiers and the custom field both resolve to a number through here, so a
 * typed "5100" and the 5100 tier compare equal.
 */
export function parseAmount(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = typeof raw === "string" ? raw.trim() : raw;
  if (trimmed === "") return null;
  const n = typeof trimmed === "number" ? trimmed : Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function localeFor(region: RegionCode): string {
  return region === "IN" ? "en-IN" : "en-US";
}

function symbolFor(region: RegionCode): string {
  return region === "IN" ? "₹" : "$";
}

/**
 * Validate a full donation payload for a region. Returns a map of field name ->
 * message containing only the fields that failed; an empty object means valid.
 */
export function validateDonation(
  values: DonationValues,
  ctx: DonationContext
): DonationErrors {
  const errors: DonationErrors = {};
  const min = MIN_AMOUNT[ctx.region];
  const amount = parseAmount(values.amount);

  if (amount === null) {
    errors.amount = "Enter a donation amount.";
  } else if (amount < min) {
    errors.amount = `Minimum donation is ${symbolFor(ctx.region)}${min.toLocaleString(
      localeFor(ctx.region)
    )}.`;
  } else if (amount > MAX_AMOUNT) {
    errors.amount = "For gifts above this amount, please contact us directly.";
  }

  const name = (values.fullName ?? "").trim();
  if (!name) errors.fullName = "Enter your full name.";
  else if (name.length < 3) errors.fullName = "Name must be at least 3 characters.";

  const email = (values.email ?? "").trim();
  if (!email) errors.email = "Enter your email address.";
  else if (!EMAIL_RE.test(email)) errors.email = "Enter a valid email address.";

  const mobileDigits = (values.mobile ?? "").replace(/\D/g, "");
  if (!mobileDigits) errors.mobile = "Enter a phone number.";
  else if (mobileDigits.length < 7) errors.mobile = "Enter a valid phone number.";

  if (ctx.region === "IN" && ctx.claim80G) {
    const pan = (values.pan ?? "").trim().toUpperCase();
    if (!pan) errors.pan = "PAN is required to claim 80G.";
    else if (!PAN_RE.test(pan)) errors.pan = "Enter a valid PAN (e.g. ABCDE1234F).";
  }

  if (!values.termsAccepted) {
    errors.termsAccepted = "Please confirm to continue.";
  }

  return errors;
}

export function isValidDonation(
  values: DonationValues,
  ctx: DonationContext
): boolean {
  return Object.keys(validateDonation(values, ctx)).length === 0;
}

export interface RhfFieldError {
  type: string;
  message: string;
}

export interface RhfResolverResult {
  values: DonationValues | Record<string, never>;
  errors: Partial<Record<keyof DonationValues, RhfFieldError>>;
}

/**
 * Adapt `validateDonation` to the react-hook-form resolver contract. RHF passes
 * the live `context` ({ region, claim80G }) on every validation pass, so the
 * rules track region/80G changes without stale closures.
 */
export function donationResolver(
  values: DonationValues,
  context: DonationContext | undefined
): RhfResolverResult {
  const ctx: DonationContext = context ?? { region: "US", claim80G: false };
  const fieldErrors = validateDonation(values, ctx);
  const errors: Partial<Record<keyof DonationValues, RhfFieldError>> = {};

  (Object.keys(fieldErrors) as Array<keyof DonationValues>).forEach((key) => {
    const message = fieldErrors[key];
    if (message) errors[key] = { type: "validation", message };
  });

  return {
    values: Object.keys(errors).length === 0 ? values : {},
    errors,
  };
}
